import React, { useState, useEffect } from 'react';
import QureLayout from '../components/qure/QureLayout';
import API from '../config/api';
import { useAuth } from '../contexts/AuthContext';

const MyTeams = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [teams, setTeams] = useState([]);
  const [stats, setStats] = useState({
    total_teams: 0,
    total_members: 0,
    team_experiments: 0,
    active_teams: 0
  });
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchTeams();
  }, []);

  const fetchTeams = async () => {
    try {
      setLoading(true);
      console.log('Fetching teams...');
      const response = await API.get('/qure/teams');
      console.log('Teams response:', response.data);
      if (response.data && response.data.ok) {
        const teamsData = response.data.teams || [];
        const statsData = response.data.stats || {
          total_teams: 0,
          total_members: 0,
          team_experiments: 0,
          active_teams: 0
        };
        console.log('Setting teams:', teamsData.length);
        console.log('Setting stats:', statsData);
        setTeams(teamsData);
        setStats(statsData);
      } else {
        console.error('Response not ok:', response.data);
        setTeams([]);
        setStats({
          total_teams: 0,
          total_members: 0,
          team_experiments: 0,
          active_teams: 0
        });
      }
    } catch (error) {
      console.error('Error fetching teams:', error);
      console.error('Error response:', error.response);
      setTeams([]);
      setStats({
        total_teams: 0,
        total_members: 0,
        team_experiments: 0,
        active_teams: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const handleViewTeam = (team) => {
    setSelectedTeam(team);
    setShowModal(true);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const formatFullDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const getInitials = (firstName, lastName) => {
    const first = firstName ? firstName[0] : '';
    const last = lastName ? lastName[0] : '';
    return (first + last).toUpperCase() || 'U';
  };

  const getMemberSince = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      year: 'numeric' 
    });
  };

  if (loading) {
    return (
      <QureLayout>
        <div className="teams-container">
          <div className="text-center py-5">
            <div className="qure-loading"></div>
            <p className="mt-3">Loading teams...</p>
          </div>
        </div>
      </QureLayout>
    );
  }

  return (
    <QureLayout>
      <div className="teams-container">
        {/* Page Header */}
        <div className="page-header">
          <div className="page-header-content">
            <div className="page-title-section">
              <h1>
                <i className="bi bi-people-fill"></i>
                My Teams
              </h1>
              <p className="page-subtitle">Teams you're a member of and their collaboration activities</p>
            </div>
            <div className="header-stats">
              <div className="stat-item">
                <span className="stat-number">{stats.total_teams}</span>
                <span className="stat-label">Teams</span>
              </div>
              <div className="stat-item">
                <span className="stat-number">{stats.team_experiments}</span>
                <span className="stat-label">Assignments</span>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="quick-stats">
          <div className="quick-stat teams">
            <div className="quick-stat-number">{stats.total_teams}</div>
            <div className="quick-stat-label">Total Teams</div>
          </div>
          <div className="quick-stat members">
            <div className="quick-stat-number">{stats.total_members}</div>
            <div className="quick-stat-label">Unique Members</div>
          </div>
          <div className="quick-stat experiments">
            <div className="quick-stat-number">{stats.team_experiments}</div>
            <div className="quick-stat-label">Team Experiments</div>
          </div>
          <div className="quick-stat active">
            <div className="quick-stat-number">{stats.active_teams}</div>
            <div className="quick-stat-label">Active Teams</div>
          </div>
        </div>

        {/* Teams Grid */}
        <div className="teams-grid">
          {teams.length > 0 ? (
            teams.map((team, index) => (
              <div key={team.group_id} className="team-card" onClick={() => handleViewTeam(team)}>
                <div className="card-header">
                  <div className="team-icon">
                    <i className="bi bi-people"></i>
                  </div>
                  <div className="team-name">{team.team_name}</div>
                  <div className="team-meta">
                    <div className="meta-item">
                      <i className="bi bi-person meta-icon"></i>
                      <span>Created by {team.created_by_name}</span>
                    </div>
                    <div className="meta-item">
                      <i className="bi bi-calendar meta-icon"></i>
                      <span>{formatDate(team.created_at)}</span>
                    </div>
                  </div>
                  
                  <div className="team-stats">
                    <div className="team-stat">
                      <span className="team-stat-number">{team.member_count}</span>
                      <span className="team-stat-label">Members</span>
                    </div>
                    <div className="team-stat">
                      <span className="team-stat-number">{team.team_experiments}</span>
                      <span className="team-stat-label">Experiments</span>
                    </div>
                    <div className="team-stat">
                      <span className="team-stat-number">{team.recent_experiments?.length || 0}</span>
                      <span className="team-stat-label">Recent</span>
                    </div>
                  </div>
                </div>
                
                <div className="card-body">
                  <div className="members-section">
                    <h6>
                      <i className="bi bi-people"></i>
                      Team Members
                    </h6>
                    <div className="members-list">
                      {team.members.slice(0, 4).map((member) => (
                        <div key={member.user_id} className="member-item">
                          <div className="member-avatar">
                            {getInitials(member.first_name, member.last_name)}
                          </div>
                          <div className="member-info">
                            <div className="member-name">
                              {`${member.first_name || ''} ${member.last_name || ''}`.trim() || 'Unknown User'}
                              {member.user_id === user?.user_id && (
                                <span className="member-badge">You</span>
                              )}
                              {member.user_id === team.creator_id && (
                                <span className="member-badge leader">Leader</span>
                              )}
                            </div>
                            <div className="member-role">{member.user_type || 'User'}</div>
                          </div>
                        </div>
                      ))}
                      {team.member_count > 4 && (
                        <div className="member-item" style={{justifyContent: 'center', fontStyle: 'italic', color: 'var(--text-light)'}}>
                          +{team.member_count - 4} more members
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {team.recent_experiments && team.recent_experiments.length > 0 && (
                    <div className="experiments-preview">
                      <h6>
                        <i className="bi bi-flask"></i>
                        Recent Team Experiments
                      </h6>
                      {team.recent_experiments.slice(0, 2).map((exp, expIndex) => (
                        <div key={expIndex} className="experiment-preview-item">
                          <div className="preview-title">{exp.draft_title}</div>
                          <span className={`experiment-status-badge ${exp.status}`}>
                            {exp.status}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                <div className="card-footer">
                  <span className="text-muted">Member since {getMemberSince(team.created_at)}</span>
                  <button className="view-team-btn">
                    <i className="bi bi-eye"></i>
                    View Details
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="empty-state">
              <i className="bi bi-people"></i>
              <h4>No Team Memberships</h4>
              <p>You haven't been added to any teams yet. Contact your qurator to be included in team-based experiments and collaborations.</p>
            </div>
          )}
        </div>
      </div>

      {/* Team Details Modal */}
      {showModal && selectedTeam && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h5 className="modal-title">
                <i className="bi bi-people me-2"></i>
                Team Details
              </h5>
              <button 
                type="button" 
                className="modal-close" 
                onClick={() => setShowModal(false)}
                aria-label="Close"
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <div className="modal-body">
              <div style={{marginBottom: '2rem'}}>
                <h6 style={{color: 'var(--qure-primary)', fontWeight: '700', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                  <i className="bi bi-info-circle"></i> Team Information
                </h6>
                <div style={{background: 'var(--bg-light)', padding: '1.5rem', borderRadius: 'var(--border-radius-small)'}}>
                  <p><strong>Team Name:</strong> {selectedTeam.team_name}</p>
                  <p><strong>Created by:</strong> {selectedTeam.created_by_name}</p>
                  <p><strong>Created on:</strong> {formatFullDate(selectedTeam.created_at)}</p>
                  <p><strong>Total Members:</strong> {selectedTeam.member_count}</p>
                  <p><strong>Team Experiments Assigned to You:</strong> {selectedTeam.team_experiments}</p>
                </div>
              </div>
              
              <div style={{marginBottom: '2rem'}}>
                <h6 style={{color: 'var(--qure-primary)', fontWeight: '700', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                  <i className="bi bi-people"></i> All Team Members ({selectedTeam.member_count})
                </h6>
                <div style={{maxHeight: '400px', overflowY: 'auto'}}>
                  {selectedTeam.members.map((member) => {
                    const initials = getInitials(member.first_name, member.last_name);
                    const fullName = `${member.first_name || ''} ${member.last_name || ''}`.trim() || 'Unknown User';
                    const isCurrentUser = member.user_id === user?.user_id;
                    const isCreator = member.user_id === selectedTeam.creator_id;
                    
                    return (
                      <div key={member.user_id} style={{display: 'flex', alignItems: 'center', gap: '1rem', padding: '1rem', background: 'var(--bg-light)', borderRadius: '10px', marginBottom: '0.75rem'}}>
                        <div style={{width: '50px', height: '50px', borderRadius: '50%', background: 'linear-gradient(135deg, var(--info-blue), var(--purple))', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: '600', fontSize: '1rem'}}>
                          {initials}
                        </div>
                        <div style={{flex: 1}}>
                          <div style={{fontWeight: '600', color: 'var(--text-dark)', marginBottom: '0.25rem'}}>
                            {fullName}
                            {isCurrentUser && (
                              <span style={{background: 'var(--qure-primary)', color: 'white', padding: '0.2rem 0.6rem', borderRadius: '15px', fontSize: '0.7rem', fontWeight: '600', marginLeft: '0.5rem'}}>YOU</span>
                            )}
                            {isCreator && (
                              <span style={{background: 'var(--qure-success)', color: 'white', padding: '0.2rem 0.6rem', borderRadius: '15px', fontSize: '0.7rem', fontWeight: '600', marginLeft: '0.5rem'}}>LEADER</span>
                            )}
                          </div>
                          <div style={{fontSize: '0.85rem', color: 'var(--text-light)', textTransform: 'uppercase', letterSpacing: '0.5px'}}>
                            {member.user_type || 'User'}
                          </div>
                          <div style={{fontSize: '0.8rem', color: 'var(--text-lighter)'}}>
                            Joined {getMemberSince(member.joined_at)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              <div style={{marginBottom: '1rem'}}>
                <h6 style={{color: 'var(--qure-primary)', fontWeight: '700', marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                  <i className="bi bi-flask"></i> Your Recent Team Experiments
                </h6>
                {selectedTeam.recent_experiments && selectedTeam.recent_experiments.length > 0 ? (
                  selectedTeam.recent_experiments.map((exp, expIndex) => {
                    const statusColor = exp.status === 'completed' ? 'var(--qure-success)' : 'var(--qure-warning)';
                    return (
                      <div key={expIndex} style={{background: 'var(--qure-primary-light)', padding: '1rem', borderRadius: '10px', marginBottom: '0.75rem', borderLeft: '4px solid var(--qure-primary)'}}>
                        <div style={{fontWeight: '600', color: 'var(--text-dark)', marginBottom: '0.5rem'}}>
                          {exp.draft_title}
                        </div>
                        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                          <span style={{color: 'var(--text-light)', fontSize: '0.9rem'}}>{exp.project_name}</span>
                          <span style={{display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.8rem', color: 'var(--text-light)'}}>
                            <span style={{width: '8px', height: '8px', borderRadius: '50%', background: statusColor}}></span>
                            {exp.status.charAt(0).toUpperCase() + exp.status.slice(1)}
                          </span>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <p style={{color: 'var(--text-light)', fontStyle: 'italic'}}>No recent experiments assigned to you from this team.</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </QureLayout>
  );
};

export default MyTeams;
